/**
 * PayFastPaymentService
 * Service for PayFast payment initiation and status operations
 * Route: api/payfast/payment
 * Controller: PayFastPaymentController.cs
 */
import { BaseService } from '../base/BaseService';
/**
 * PayFastPaymentService
 * Handles PayFast payment initiation, status queries, and return/cancel callbacks
 * Maps to /api/payfast/payment endpoint
 */
export class PayFastPaymentService extends BaseService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? PayFastPaymentService.DEFAULT_SERVICE_PATH,
            basePath: PayFastPaymentService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    /**
     * Apply defaults to the payment request before sending.
     * Ensures paymentMethodID defaults to 1 (CARD) if not specified.
     */
    withDefaults(request) {
        return {
            ...request,
            paymentMethodID: request.paymentMethodID ?? 1,
        };
    }
    // ==================== PAYMENT INITIATION ====================
    /**
     * Initiate a generic payment (caller must set categoryID)
     * POST /api/payfast/payment/initiate
     */
    async initiate(request) {
        const response = await this.initiateRaw(request);
        if (!response.data) {
            throw new Error('Failed to initiate payment');
        }
        return response.data;
    }
    initiateRaw(request) {
        const endpoint = this.getEndpoint('initiate', 'initiate');
        const url = this.buildUrl(endpoint);
        return this.http.post(url, this.withDefaults(request));
    }
    /**
     * Initiate a giving/tithe payment (CategoryID=1)
     * POST /api/payfast/payment/give
     */
    async give(request) {
        const response = await this.giveRaw(request);
        if (!response.data) {
            throw new Error('Failed to initiate giving payment');
        }
        return response.data;
    }
    giveRaw(request) {
        const endpoint = this.getEndpoint('give', 'give');
        const url = this.buildUrl(endpoint);
        return this.http.post(url, this.withDefaults(request));
    }
    /**
     * Initiate a purchase payment (CategoryID=2)
     * POST /api/payfast/payment/purchase
     */
    async purchase(request) {
        const response = await this.purchaseRaw(request);
        if (!response.data) {
            throw new Error('Failed to initiate purchase payment');
        }
        return response.data;
    }
    purchaseRaw(request) {
        const endpoint = this.getEndpoint('purchase', 'purchase');
        const url = this.buildUrl(endpoint);
        return this.http.post(url, this.withDefaults(request));
    }
    /**
     * Initiate a donation payment (CategoryID=3)
     * POST /api/payfast/payment/donate
     */
    async donate(request) {
        const response = await this.donateRaw(request);
        if (!response.data) {
            throw new Error('Failed to initiate donation payment');
        }
        return response.data;
    }
    donateRaw(request) {
        const endpoint = this.getEndpoint('donate', 'donate');
        const url = this.buildUrl(endpoint);
        return this.http.post(url, this.withDefaults(request));
    }
    /**
     * Initiate a service fee payment (CategoryID=5)
     * POST /api/payfast/payment/service
     */
    async service(request) {
        const response = await this.serviceRaw(request);
        if (!response.data) {
            throw new Error('Failed to initiate service fee payment');
        }
        return response.data;
    }
    serviceRaw(request) {
        const endpoint = this.getEndpoint('service', 'service');
        const url = this.buildUrl(endpoint);
        return this.http.post(url, this.withDefaults(request));
    }
    /**
     * Initiate a vendor store purchase (CategoryID=7)
     * POST /api/payfast/payment/vendor
     */
    async vendor(request) {
        const response = await this.vendorRaw(request);
        if (!response.data) {
            throw new Error('Failed to initiate vendor payment');
        }
        return response.data;
    }
    vendorRaw(request) {
        const endpoint = this.getEndpoint('vendor', 'vendor');
        const url = this.buildUrl(endpoint);
        return this.http.post(url, this.withDefaults(request));
    }
    // ==================== PAYMENT STATUS ====================
    /**
     * Get payment status by TransactionID (GUID)
     * GET /api/payfast/payment/{transactionId}/status
     */
    async getStatusById(transactionId) {
        const response = await this.getStatusByIdRaw(transactionId);
        if (!response.data) {
            throw new Error(`Payment status for transaction ${transactionId} not found`);
        }
        return response.data;
    }
    getStatusByIdRaw(transactionId) {
        const url = this.buildUrl(`${transactionId}/status`);
        return this.http.get(url);
    }
    /**
     * Get payment status by merchant reference (m_payment_id)
     * GET /api/payfast/payment/status/{paymentId}
     */
    async getStatusByPaymentId(paymentId) {
        const response = await this.getStatusByPaymentIdRaw(paymentId);
        if (!response.data) {
            throw new Error(`Payment status for paymentId ${paymentId} not found`);
        }
        return response.data;
    }
    getStatusByPaymentIdRaw(paymentId) {
        const url = this.buildUrl(`status/${paymentId}`);
        return this.http.get(url);
    }
    // ==================== RETURN / CANCEL HANDLERS ====================
    /**
     * Handle payment return callback
     * GET /api/payfast/payment/return
     */
    async paymentReturn(paymentId) {
        const response = await this.paymentReturnRaw(paymentId);
        if (!response.data) {
            throw new Error('Failed to handle payment return');
        }
        return response.data;
    }
    paymentReturnRaw(paymentId) {
        const url = this.buildUrlWithParams('return', paymentId ? { m_payment_id: paymentId } : undefined);
        return this.http.get(url);
    }
    /**
     * Handle payment cancel callback
     * GET /api/payfast/payment/cancel
     */
    async paymentCancel(paymentId) {
        const response = await this.paymentCancelRaw(paymentId);
        if (!response.data) {
            throw new Error('Failed to handle payment cancel');
        }
        return response.data;
    }
    paymentCancelRaw(paymentId) {
        const url = this.buildUrlWithParams('cancel', paymentId ? { m_payment_id: paymentId } : undefined);
        return this.http.get(url);
    }
}
PayFastPaymentService.DEFAULT_SERVICE_PATH = 'payments';
PayFastPaymentService.BASE_PATH = 'api/payfast/payment';
//# sourceMappingURL=PayFastPaymentService.js.map